import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { User } from '@app/_models';
import { Role } from '@app/_models/account/role';
import { PageRoleMapping } from '@app/_models/account/PageRoleMapping';

@Injectable({ providedIn: 'root' })
export class AccountService {
    private userSubject: BehaviorSubject<User | null>;
    private pageRoleMappingSubject: BehaviorSubject<PageRoleMapping[] | null>;

    public user: Observable<User | null>;
    public pageRoleMappings: Observable<PageRoleMapping[] | null>;

    constructor(
        private router: Router,
        private http: HttpClient
    ) {
        this.userSubject = new BehaviorSubject(JSON.parse(localStorage.getItem('user')!));
        this.pageRoleMappingSubject = new BehaviorSubject(JSON.parse(localStorage.getItem('pageRoleMappings')!));

        this.user = this.userSubject.asObservable();
        this.pageRoleMappings = this.pageRoleMappingSubject.asObservable();

    }

    public get userValue() {
        return this.userSubject.value;
    }

    public get pageRoleMappingsValue() {
        return this.pageRoleMappingSubject.value;
    }

    public hasPermission(page: string, permission: 'canRead' | 'canUpdate' | 'canDelete' | 'canAdd'): boolean {
        const mapping = this.pageRoleMappingsValue?.find(m => m.page?.name === page);
        return mapping ? mapping[permission] : false;
    }

    public canBulkImport(): boolean {
        console.log(this.hasPermission('products', 'canAdd'));        
        return this.hasPermission('products', 'canAdd');
    }

    private getHttpOptions() {
        const user = this.userValue;
        
        let selectedCompanyId: string;
        let selectedBranchId: string;
        
        // Only use localStorage for admin users, non-admin users use their assigned company/branch
        if (this.isAdmin()) {
            selectedCompanyId = localStorage.getItem('selectedCompanyId') || 
                              user?.companyId?.toString() || '1';
            selectedBranchId = localStorage.getItem('selectedBranchId') || 
                             user?.branchId?.toString() || '1';
        } else {
            // Non-admin users always use their assigned company/branch
            selectedCompanyId = user?.companyId?.toString() || '1';
            selectedBranchId = user?.branchId?.toString() || '1';
        }
        
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Company-Id': selectedCompanyId,
                'X-Branch-Id': selectedBranchId
            })
        };
    }

    login(username: string, password: string) {
        return this.http.post<User>(`${environment.apiUrl}/users/authenticate`, { username, password })
            .pipe(
                switchMap(user => {
                    // Store user details and JWT token in local storage
                    localStorage.setItem('user', JSON.stringify(user));
                    this.userSubject.next(user);

                    // Clear admin selections if this is not an admin user
                    if (user.roleId !== 1 && user.roleId !== 3) {
                        localStorage.removeItem('selectedCompanyId');
                        localStorage.removeItem('selectedBranchId');
                    }

                    // Fetch and store page role mappings for the user with proper headers
                    return this.http.get<PageRoleMapping[]>(`${environment.apiUrl}/users/GetPageRoleMappings?roleId=${user.roleId}`, this.getHttpOptions()).pipe(
                        map(mappings => {
                            localStorage.setItem('pageRoleMappings', JSON.stringify(mappings));
                            this.pageRoleMappingSubject.next(mappings);
                            return user;
                        })
                    );
                })
            );
    }

    logout() {
        // remove user from local storage and set current user to null
        localStorage.removeItem('user');
        localStorage.removeItem('pageRoleMappings');
        // Clear admin selections on logout
        localStorage.removeItem('selectedCompanyId');
        localStorage.removeItem('selectedBranchId');
        
        this.pageRoleMappingSubject.next(null);
        this.userSubject.next(null);
        this.router.navigate(['/account/login']);
    }

    isAdmin(): boolean {
        return this.userValue?.roleId === 1 || this.userValue?.roleId === 3 || this.userValue?.roleId === 6;
    }

    isSuperAdmin(): boolean {
        return this.userValue?.isSuperAdmin === true || this.userValue?.roleId === 1;
    }

    isCompanyAdmin(): boolean {
        return this.userValue?.isCompanyAdmin === true || this.userValue?.roleId === 6;
    }

    isTransporter(): boolean {
        return this.userValue?.roleId === 5; // Transporter role
    }

    isSales(): boolean {
        return this.userValue?.roleId === 2; // Sales role
    }

    register(user: User) {
        return this.http.post(`${environment.apiUrl}/users/register`, user);
    }

    getAll() {
        return this.http.get<User[]>(`${environment.apiUrl}/users`, this.getHttpOptions());
    }

    getPages() {
        return this.http.get<any[]>(`${environment.apiUrl}/users`, this.getHttpOptions());
    }

    getPageRoleMappings() {
        return this.http.get<any[]>(`${environment.apiUrl}/users/GetPageRoleMappings`, this.getHttpOptions());
    }

    updatePageRoleMapping(mapping: PageRoleMapping) {
        return this.http.put(`${environment.apiUrl}/users/pageRoleMappings/${mapping.id}`, mapping, this.getHttpOptions());
    }

    getRoles() {
        return this.http.get<Role[]>(`${environment.apiUrl}/users/roles`, this.getHttpOptions());
    }

    getById(id: string) {
        return this.http.get<User>(`${environment.apiUrl}/users/${id}`, this.getHttpOptions());
    }



    update(id: string, params: any) {
        return this.http.put(`${environment.apiUrl}/users/${id}`, params, this.getHttpOptions())
            .pipe(map(x => {
                // update stored user if the logged in user updated their own record
                if (id == this.userValue?.id) {
                    // update local storage
                    const user = { ...this.userValue, ...params };
                    localStorage.setItem('user', JSON.stringify(user));

                    // publish updated user to subscribers
                    this.userSubject.next(user);
                }
                return x;
            }));
    }

    changePassword(currentPassword: string, newPassword: string) {
        return this.http.put(`${environment.apiUrl}/users/change-password`, { currentPassword, newPassword });
    }

    updateProfile(firstName: string, lastName: string) {
        return this.http.put(`${environment.apiUrl}/users/profile`, { firstName, lastName })
            .pipe(map(x => {
                const user = { ...this.userValue, firstName, lastName };
                localStorage.setItem('user', JSON.stringify(user));
                this.userSubject.next(user);
                return x;
            }));
    }

    delete(id: string) {
        return this.http.delete(`${environment.apiUrl}/users/${id}`, this.getHttpOptions())
            .pipe(map(x => {
                // auto logout if the logged in user deleted their own record
                if (id == this.userValue?.id) {
                    this.logout();
                }
                return x;
            }));
    }

    // Method to update selected company and branch for admin users
    updateCompanyBranchSelection(companyId: string, branchId: string) {
        if (this.isAdmin()) {
            localStorage.setItem('selectedCompanyId', companyId);
            localStorage.setItem('selectedBranchId', branchId);
        }
    }

    // Method to get current company/branch selection
    getCurrentCompanyBranchSelection() {
        const user = this.userValue;
        
        // Super admin uses localStorage selection
        if (this.isSuperAdmin()) {
            return {
                companyId: localStorage.getItem('selectedCompanyId') || '',
                branchId: localStorage.getItem('selectedBranchId') || ''
            };
        } 
        // Company admin uses their assigned company, but can select branches
        else if (this.isCompanyAdmin()) {
            return {
                companyId: user?.companyId?.toString() || '',
                branchId: localStorage.getItem('selectedBranchId') || ''
            };
        } 
        // Regular users use their assigned company/branch
        else {
            return {
                companyId: user?.companyId?.toString() || '',
                branchId: user?.branchId?.toString() || ''
            };
        }
    }
}